<script setup lang="ts">
import { computed, nextTick, ref, toRefs, watch } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/api';
import { patentPublicationOffice } from '@/api/document';
import { PageName } from '@/api/tracking';
import AdpDocumentActionBar from '@/components/adp-document/actions/adp-document-action-bar.vue';
import AdpDocumentPestCategories from '@/components/adp-document/adp-document-pest-categories.vue';
import AdpDocumentAllianceMedia from '@/components/common/adp-document/adp-document-alliance-media.vue';
import AdpDocumentBasicInfo from '@/components/common/adp-document/basic-info.vue';
import CommentList from '@/components/common/comment/comment-list.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import Content from '@/components/layouts/content.vue';
import CommentCreateModal from '@/components/modals/comment-create-modal.vue';
import CommentEditModal from '@/components/modals/comment-edit-modal.vue';
import DialogGroupMark from '@/components/modals/dialog-group-mark.vue';
import MarkModal from '@/components/modals/mark-modal.vue';
import { ActionModal, Comment, DocType, Group, Mention } from '@/types';
import { useComment } from '@/utils/actions/comment';
import { useGroupMark } from '@/utils/actions/group-mark';
import { useMark } from '@/utils/actions/mark';
import {
  articleClickedEvent,
  articleContextMenuClickedEvent,
} from '@/utils/actions/view';
import { getSiteType } from '@/utils/adpDocument';
import { useAdpDocumentMarkPresentation } from '@/utils/composables/useAdpDocumentMarkPresentation';
import {
  copyLinkOptionAction,
  useCopyDocumentLink,
} from '@/utils/composables/useCopyDocumentLink';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { useNewsCategories } from '@/utils/composables/useNewsCategories';
import { STATES, useDocument, useThemeList, useUserInfo } from '@/utils/swr';
import { userSession } from '@/utils/userSession';
import { useEmitter, useStore } from '@/utils/vue';

const props = defineProps<{
  id: string;
  // eslint-disable-next-line vue/prop-name-casing
  doc_type: DocType;
}>();

const route = useRoute();
const store = useStore();
const emitter = useEmitter();
const pageName: PageName = 'article';
const { getCurrentPathForTracking } = useCurrentPathForTracking();
const { createSnackbar } = useSnackbar();
const { createMark, removeMark } = useMark({
  markSource: 'article',
  trackingBaseData: {
    pageName: pageName,
    pageUrl: getCurrentPathForTracking(),
  },
});

const { createGroupMark } = useGroupMark({
  markSource: 'article',
  trackingBaseData: {
    pageName: pageName,
    pageUrl: getCurrentPathForTracking(),
  },
});

const { createComment, updateComment, removeComment } = useComment({
  trackingBaseData: {
    pageName: pageName,
    pageUrl: getCurrentPathForTracking(),
  },
});

const actionModal = ref<ActionModal>(undefined);

const feedType = computed(() => store.state.feedType.feedType);

const { data: userInfo } = useUserInfo();
const teamInfo = computed(() => store.state.teamInfo.teamInfo);
const enableAllianceMedia = computed(
  () => teamInfo.value?.enable_alliance_media ?? false,
);

const { id, doc_type } = toRefs(props);
const documentParams = computed(() => ({
  article_lang: route.query.article_lang as string | undefined,
  patent_publication_office: route.query.patent_publication_office as
    | string
    | undefined,
  report_page_number: route.query.report_page_number
    ? Number(route.query.report_page_number)
    : undefined,
}));
const {
  data: adpDocument,
  state,
  mutate,
} = useDocument(id, doc_type, documentParams);
const { pestCategories, newsCategoriesByPest } = useNewsCategories({
  adpDocument,
});
const hasPestCategories = computed(() => {
  if (!pestCategories.value) {
    return false;
  }
  return pestCategories.value.length > 0;
});

const { data: themes, state: themeState } = useThemeList(feedType);
const theme = computed(() =>
  themes.value?.themes.find(t => t.id === Number(route.query.themeId)),
);

const selectedComment = ref<Comment | undefined>(undefined);
const isPostingComment = ref(false);

const showMarkModal = () => {
  actionModal.value = 'mark';
};

const showCreateCommentModal = () => {
  actionModal.value = 'comment';
};

const showGroupMarkModal = () => {
  actionModal.value = 'group_mark';
};

const showEditCommentModal = (comment: Comment) => {
  selectedComment.value = comment;
  actionModal.value = 'commentEdit';
};

const closeActionModal = () => {
  actionModal.value = undefined;
  selectedComment.value = undefined;
};

watch(
  () => [state.value, themeState.value],
  () => {
    if (state.value === STATES.SUCCESS && themeState.value === STATES.SUCCESS) {
      const modal = route.query.modal;
      switch (modal) {
        case 'mark':
          if (!hasMyMark.value) {
            showMarkModal();
          } else {
            createSnackbar({
              message: 'この記事は既にマークされています',
              type: 'success',
            });
          }
          break;
        case 'group_mark':
          showGroupMarkModal();
          break;
        case 'comment':
          showCreateCommentModal();
          break;
        default:
          break;
      }
    }
  },
);

const isMarkingArticle = ref(false);

const hasMyMark = computed(() =>
  adpDocument.value?.marks.find(m => m.user_id === userInfo.value?.id),
);

const groupComments = computed(() => {
  if (!comments.value || comments.value.length === 0) return [];
  let groupIds = comments.value.map(g => g.group_id);
  groupIds = [...new Set(groupIds)];
  groupIds = groupIds?.sort((x, y) => {
    if (x < y) return -1;
    if (x > y) return 1;
    return 0;
  });

  const groupComments: { group_id: number; comments: Comment[] }[] = [];
  groupIds?.forEach(groupId => {
    const groupComment = (comments.value ?? []).filter(
      comment => comment.group_id === groupId,
    );
    groupComments.push({ group_id: groupId, comments: groupComment });
  });
  return groupComments;
});

const comments = computed(() => adpDocument.value?.comments ?? []);

const { isMarked, markedUsers, markedGroups, commentedUsers } =
  useAdpDocumentMarkPresentation(adpDocument);
const { copyLink } = useCopyDocumentLink();
const optionActions = [copyLinkOptionAction];
const handleOptionAction = () => {
  if (adpDocument.value) copyLink(adpDocument.value);
};

const markArticle = async (markProps?: { tags?: string[]; memo?: string }) => {
  if (isMarkingArticle.value || !userInfo.value || !adpDocument.value) return;
  isMarkingArticle.value = true;

  try {
    if (hasMyMark.value) {
      await removeMark({
        adpDocument: adpDocument.value,
        userInfo: userInfo.value,
      });
    } else {
      await createMark({
        adpDocument: adpDocument.value,
        userInfo: userInfo.value,
        tags: markProps?.tags,
        memo: markProps?.memo,
      });
    }
    closeActionModal();
  } finally {
    await mutate();
    nextTick(() => (isMarkingArticle.value = false));
  }
};

const groupMarkArticle = async ({
  group,
  tags,
  memo,
}: {
  group: Group;
  tags?: string[];
  memo?: string;
}) => {
  if (!adpDocument.value) return;

  await createGroupMark({
    adpDocument: adpDocument.value,
    group: group,
    tags: tags,
    memo: memo,
  });
  closeActionModal();
  emitter.emit('article-updated', adpDocument.value);
};

const isAllianceMedia = computed(() => {
  if (!adpDocument.value) return false;
  return (
    'is_alliance_media' in adpDocument.value &&
    adpDocument.value.is_alliance_media
  );
});

const comment = async ({
  group,
  content,
  mention,
}: {
  group: Group;
  content: string;
  mention?: Mention;
}) => {
  try {
    isPostingComment.value = true;
    const createdComment = await createComment({
      groupId: group.id,
      adpDocument: adpDocument.value,
      content,
      mention,
    });
    emitter.emit('article-updated', adpDocument.value);
    emitter.emit('comment-created', {
      ...createdComment,
      article: adpDocument.value,
    });
    closeActionModal();
  } finally {
    isPostingComment.value = false;
  }
};

const editComment = async ({
  comment,
  content,
}: {
  comment: Comment;
  content: string;
}) => {
  if (!adpDocument.value) return;

  try {
    isPostingComment.value = true;
    const updatedComment = await updateComment({
      adpDocument: adpDocument.value,
      comment: comment,
      content: content,
    });
    emitter.emit('comment-updated', updatedComment);
    closeActionModal();
  } finally {
    isPostingComment.value = false;
  }
};

const deleteComment = async (comment: Comment) => {
  const deleteAction = async () => {
    await removeComment({
      adpDocument: comment.article,
      comment: comment,
    });
  };
  const payload = {
    headerText: 'コメントを削除しますか？',
    bodyText: '',
    action: deleteAction,
  };
  store.commit('confirmationModal/showConfirmationModal');
  store.commit('confirmationModal/setTextAndAction', payload);
};

// NOTE: teamInfoをstoreで管理しているため監視対象に入れている。emailから直接このpageに来る可能性があるため
watch(
  () => state.value === STATES.SUCCESS && teamInfo.value && adpDocument.value,
  async () => {
    // TSのガード節として記載している
    if (!adpDocument.value) return;
    await api.trackIntegration('article', route, {
      article: {
        id: adpDocument.value.id,
        doc_type: doc_type.value,
        lang: adpDocument.value.lang,
        patent_publication_office: patentPublicationOffice(adpDocument.value),
      },
    });
    if (isAllianceMedia.value) {
      if ('media_name' in adpDocument.value) {
        await api.trackPageView({
          pageName: pageName,
          pageUrl: route.path,
          adpDocument: {
            id: Number(id.value),
            doc_type: doc_type.value,
            alliance_media: adpDocument.value.media_name,
            enable_alliance_media_body: enableAllianceMedia.value,
          },
        });

        // adp-document-card-link.vueから遷移してきた際には、 article_viewは登録しない
        if (userSession.getIsOpenedFromAdpDocumentCardLink()) {
          userSession.setIsOpenedFromAdpDocumentCardLink(false);
          return;
        }
        await api.sendView({
          adpDocument: adpDocument.value,
          trackingBaseData: {
            pageName: pageName,
            pageUrl: route.path,
          },
          enableAllianceMedia: enableAllianceMedia.value,
        });
      }
    } else {
      await api.trackPageView({
        pageName: pageName,
        pageUrl: route.path,
        adpDocument: {
          id: Number(id.value),
          doc_type: doc_type.value,
        },
      });
    }
  },
  { once: true },
);

const sendViewProps = computed(() => ({
  adpDocument: adpDocument.value,
  themeId: theme.value ? theme.value.id : undefined,
  trackingBaseData: {
    pageName: pageName,
    pageUrl: getCurrentPathForTracking(),
  },
  siteType: getSiteType(adpDocument.value),
  enableAllianceMedia: enableAllianceMedia.value,
}));
</script>

<template>
  <Content>
    <div class="document-container" v-if="adpDocument">
      <div class="document-content">
        <div class="document-card">
          <AdpDocumentBasicInfo
            v-if="!isAllianceMedia"
            class="info"
            :adp-document="adpDocument"
            size="l"
            image-variant="banner"
            @article-link-clicked="
              articleClickedEvent(sendViewProps, adpDocument)
            "
            @article-link-context-menu="
              articleContextMenuClickedEvent(sendViewProps, $event, adpDocument)
            "
          />
          <AdpDocumentAllianceMedia
            v-else
            :adp-document="adpDocument"
            :team-info="teamInfo"
            :enable-alliance-media="enableAllianceMedia"
          ></AdpDocumentAllianceMedia>
          <AdpDocumentActionBar
            class="user-actions"
            :adp-document="adpDocument"
            :is-marked="isMarked"
            :marked-users="markedUsers"
            :marked-groups="markedGroups"
            :commented-users="commentedUsers"
            :option-actions="optionActions"
            :disabled="!enableAllianceMedia && isAllianceMedia"
            :mark-handler="markArticle"
            @option-action="handleOptionAction"
            @handle-popup-menu-click="showMarkModal"
            @handle-comment-click="showCreateCommentModal"
            @handle-group-mark-click="showGroupMarkModal"
          ></AdpDocumentActionBar>
        </div>

        <template v-if="hasPestCategories && newsCategoriesByPest">
          <div class="pest-categories-container">
            <AdpDocumentPestCategories
              :news-categories-by-pest="newsCategoriesByPest"
            />
          </div>
        </template>

        <div class="comments" v-if="groupComments.length > 0">
          <div
            class="comment-list"
            v-for="groupComment in groupComments"
            :key="groupComment.group_id"
          >
            <CommentList
              v-if="groupComment.comments && groupComment.comments.length > 0"
              :adp-document="adpDocument"
              :comments="groupComment.comments"
              :show-group="true"
              :is-summary-group="true"
              :show-comment-widget="true"
              :group-id="groupComment.group_id"
              :show-join-group-button="true"
              :page-name="pageName"
              feature="group_comments"
              @edit-comment="showEditCommentModal"
              @delete-comment="deleteComment"
            ></CommentList>
          </div>
        </div>
        <div class="no-comments" v-else>
          <div class="document-text-content">
            <div class="c-text-regular--m">
              この記事に対するコメントはまだありません
            </div>
            <div class="spacing-12"></div>
            <button
              class="c-btn c-btn--auto c-btn--AnewsPrimary"
              @click="showCreateCommentModal"
            >
              コメントする
            </button>
          </div>
        </div>
      </div>
    </div>
    <MarkModal
      v-if="actionModal === 'mark' && adpDocument"
      :adp-document="adpDocument"
      :page-name="pageName"
      mark-source="article"
      @submit="markArticle"
      @close="closeActionModal"
    ></MarkModal>
    <DialogGroupMark
      v-else-if="actionModal === 'group_mark' && adpDocument"
      :adp-document="adpDocument"
      :page-name="pageName"
      mark-source="article"
      @submit="groupMarkArticle"
      @close="closeActionModal"
    ></DialogGroupMark>
    <CommentCreateModal
      v-else-if="actionModal === 'comment' && adpDocument"
      :adp-document="adpDocument"
      :page-name="pageName"
      :is-posting-comment="isPostingComment"
      @submit="comment"
      @close="closeActionModal"
    ></CommentCreateModal>
    <CommentEditModal
      v-else-if="actionModal === 'commentEdit' && selectedComment"
      :comment="selectedComment"
      v-model:is-posting-comment="isPostingComment"
      @submit="editComment"
      @close="closeActionModal"
    ></CommentEditModal>
  </Content>
</template>

<style lang="scss" scoped>
.document-container {
  width: 100%;
  margin-top: -8px;

  .document-content {
    display: flex;
    flex-direction: column;
    width: 616px;

    .document-card {
      --document-inset: 16px;

      border-radius: 4px;
      background-color: #fff;
      border: 1px solid $color-gray400;
      margin-bottom: 24px;

      .user-actions {
        margin: 0 var(--document-inset) 4px;
      }

      &.alliance-media {
        padding: 20px 16px 0 16px;
      }
    }

    .comment-list {
      border-radius: 4px;
      background-color: #fff;
      border: 1px solid $color-gray400;
      padding: 12px 16px;
      margin-bottom: 24px;
    }

    .no-comments {
      padding: 0 16px;
      display: flex;
      justify-content: center;
      align-items: center;

      .document-text-content {
        display: flex;
        flex-direction: column;
        align-items: center;
      }
    }
  }

  .pest-categories-container {
    margin-bottom: 24px;
  }
}
</style>
